/**
 * Utilitaire pour récupérer et formater la version de l'application
 */

import { u2000ShiftCivilYmd, u2000TodayOrgYmd } from './U2000DateTimeHelpers';

const CIVIL_YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Valide un jour civil `yyyy-MM-dd` (rejette 2026-13-01, 2026-02-31, etc.).
 */
export function U2000IsCivilYmd(value: string | null | undefined): boolean {
  if (value == null) {
    return false;
  }
  const m = CIVIL_YMD_RE.exec(value.trim());
  if (!m) {
    return false;
  }
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const dt = new Date(Date.UTC(y, mo - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() + 1 === mo && dt.getUTCDate() === d;
}

/**
 * Récupère les composants de la version depuis les variables d'environnement
 * @returns Objet contenant major, minor et build
 */
export function U2000GetVersionParts(): { major: number; minor: number; build: number } {
  const major = parseInt(import.meta.env.VITE_APP_VERSION_MAJOR || '0', 10);
  const minor = parseInt(import.meta.env.VITE_APP_VERSION_MINOR || '0', 10);
  const build = parseInt(import.meta.env.VITE_APP_VERSION_BUILD || '0', 10);

  return {
    major: isNaN(major) ? 0 : major,
    minor: isNaN(minor) ? 0 : minor,
    build: isNaN(build) ? 0 : build,
  };
}

/**
 * Récupère la version formatée au format "majeur.mineur.build"
 * @returns Version formatée (ex: "1.0.0")
 */
export function U2000GetVersionString(): string {
  const { major, minor, build } = U2000GetVersionParts();
  return `${major}.${minor}.${build}`;
}

/**
 * Récupère la version formatée avec le préfixe "v"
 * @returns Version formatée avec préfixe (ex: "v1.0.0")
 */
export function U2000GetVersionWithPrefix(): string {
  return `v${U2000GetVersionString()}`;
}

/**
 * Chemin HTML des notes de version de la build courante.
 */
export function U2000GetVersionNotesPath(versionWithPrefix: string = U2000GetVersionWithPrefix()): string {
  return `/assets/docs/release/${versionWithPrefix}/index.html`;
}

/**
 * Jour civil `released_at` injecté au build (`VITE_APP_VERSION_RELEASED_AT`).
 * Vide / invalide → `null` (pas de pastille NEW).
 */
export function U2000GetVersionReleasedAtYmd(): string | null {
  const raw = String(import.meta.env.VITE_APP_VERSION_RELEASED_AT ?? '').trim();
  return U2000IsCivilYmd(raw) ? raw : null;
}

/**
 * Pastille NEW : vrai si `nowYmd` est dans `[releasedAt, releasedAt + days]` (jours civils, bornes incluses).
 */
export function U2000IsVersionNew(
  releasedAt?: string | null,
  nowYmd: string = u2000TodayOrgYmd(),
  days: number = 10
): boolean {
  if (typeof releasedAt !== 'string' || !U2000IsCivilYmd(releasedAt) || !U2000IsCivilYmd(nowYmd)) {
    return false;
  }
  const released = releasedAt.trim();
  const now = nowYmd.trim();
  const end = u2000ShiftCivilYmd(released, days);
  if (!end) {
    return false;
  }
  return now >= released && now <= end;
}
